import { Fragment, ReactNode } from "react";
import { Loader2, LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { DialogTrigger } from "@/components/ui/dialog";

import { P } from "./typo";
import { Button } from "./ui/button";
import { ENV_VARIABLES } from "@/lib/constant";

type ButtonProps = {
  LucideIcon?: LucideIcon;
  children?: ReactNode;
  className?: string;
  label?: string;
  type?: "button" | "submit" | "reset" | undefined;
  onPress?: (e: any) => void;
  loading?: boolean;
  disabled?: boolean;
  spin?: boolean;
  trigger?: boolean;
  name?: string;
  weight?: string;
};

const customButton = (buttonType: string) => {
  const ButtonComponent = ({
    LucideIcon,
    children,
    className,
    label,
    onPress,
    type,
    loading,
    disabled,
    spin,
    trigger,
    name,
    weight,
  }: ButtonProps) => {
    const primaryStyle = cn(
      "bg-primary hover:bg-primary-hover",
      "flex rounded-lg p-2 px-4 gap-2 ",
    );

    const buttonStyles =
      buttonType === "primary"
        ? primaryStyle
        : buttonType === "secondary"
          ? "flex rounded-lg p-2 px-4 gap-2 bg-secondary hover:bg-secondary-hover"
          : buttonType === "outlined"
            ? " cursor-pointer bg-primary-foreground text-foreground hover:bg-accent-hover p-1 h-max"
            : "flex rounded-lg p-2 bg-primary-hover hover:bg-primary text-background gap-2";

    const Btn = trigger ? DialogTrigger : Button;

    return (
      <Btn
        disabled={loading ? true : disabled}
        className={cn(
          "flex h-[40px] text-background items-center",
          buttonStyles,
          className,
        )}
        onClick={onPress}
        type={type}
        {...(trigger ? {} : { name })}
      >
        {loading ? (
          <Fragment>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Please wait
          </Fragment>
        ) : (
          <Fragment>
            {LucideIcon && (
              <LucideIcon
                size={16}
                className={cn(spin ? "animate-spin" : "")}
              />
            )}
            <P
              className={cn(
                "gap-2 whitespace-nowrap capitalize text-background",
                buttonType === "outlined"
                  ? "font-normal text-none"
                  : "font-medium",
                weight && `font-${weight}`,
              )}
            >
              {label}
            </P>
            {children}
          </Fragment>
        )}
      </Btn>
    );
  };
  ButtonComponent.displayName = `${
    buttonType.charAt(0).toUpperCase() + buttonType.slice(1)
  }Button`;

  return ButtonComponent;
};

export const PrimaryButton = customButton("primary");
export const SecondaryButton = customButton("secondary");
export const OutlinedButton = customButton("outlined");
